/* global theme */

if (!theme.customOptionFees) {
  theme.customOptionIsActive = (option) => {
    const type = option.dataset.customOptionType;

    if (type === 'checkbox') {
      return Boolean(option.querySelector('input[type="checkbox"]')?.checked);
    }

    if (type === 'dropdown') {
      const select = option.querySelector('select');
      return Boolean(select && select.value.trim() !== '');
    }

    const field = option.querySelector('input[type="text"], textarea');
    return Boolean(field && field.value.trim() !== '');
  };

  theme.customOptionFees = (root, formId) => {
    if (!root) return [];

    const fees = [];
    const formIdString = typeof formId === 'string' ? formId : '';
    root.querySelectorAll('[data-custom-option-variant]').forEach((option) => {
      if (formIdString && option.dataset.customOptionForm !== formIdString) return;
      if (!theme.customOptionIsActive(option)) return;

      const id = option.dataset.customOptionVariant;
      const price = Number(option.dataset.customOptionPrice || 0);
      if (id && price > 0) fees.push({ id, price });
    });

    return fees;
  };

  const formatMoney = (cents, format) => {
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = format && format.match(placeholder);
    if (!match) return (cents / 100).toFixed(2);

    const formatWithDelimiters = (number, precision, thousands, decimal) => {
      const amount = (number / 100).toFixed(precision);
      const parts = amount.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      const remainder = parts[1] ? decimal + parts[1] : '';
      return dollars + remainder;
    };

    let value = '';
    switch (match[1]) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ', '.');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2, ',', '.');
    }

    return format.replace(placeholder, value);
  };

  const basePrice = (priceEl) => {
    const variantPrice = Number(priceEl.dataset.variantPrice);
    if (!Number.isFinite(variantPrice)) return 0;

    const percent = Number(priceEl.dataset.discountPercent || 0);
    const amount = Number(priceEl.dataset.discountAmount || 0);
    let current = variantPrice;

    if (percent > 0) current -= variantPrice * (percent / 100);
    else if (amount > 0) current -= amount;

    return Math.max(0, Math.round(current));
  };

  const moneyFormatFor = (root) => {
    return root.dataset.moneyFormat
      || root.closest('.js-product')?.dataset.moneyFormat
      || document.querySelector('.js-product[data-money-format]')?.dataset.moneyFormat
      || theme.settings.moneyWithCurrencyFormat;
  };

  const updateSectionPrices = (root) => {
    if (!root) return;

    const extra = theme.customOptionFees(root).reduce((sum, fee) => sum + fee.price, 0);
    const format = moneyFormatFor(root);

    root.querySelectorAll('.js-product-price > .price, .js-atc-price > .price').forEach((priceEl) => {
      const valueEl = priceEl.querySelector('.price__current .js-value');
      if (!valueEl) return;

      if (extra <= 0) {
        if (priceEl.dataset.surchargeApplied === 'true' && priceEl.dataset.baseHtml) {
          valueEl.innerHTML = priceEl.dataset.baseHtml;
        }
        delete priceEl.dataset.surchargeApplied;
        return;
      }

      if (priceEl.dataset.surchargeApplied !== 'true') {
        priceEl.dataset.baseHtml = valueEl.innerHTML;
      }

      const formatted = formatMoney(basePrice(priceEl) + extra, format).replace(/<[^>]*>/g, '');
      const saleEl = valueEl.querySelector('.price-sale');
      if (saleEl) saleEl.textContent = formatted;
      else valueEl.textContent = formatted;
      priceEl.dataset.surchargeApplied = 'true';
    });
  };

  const priceRoot = (node) => node.closest('quick-add-drawer') || node.closest('.js-product');

  const onOptionInput = (event) => {
    const option = event.target.closest?.('[data-custom-option-variant]');
    if (!option) return;
    updateSectionPrices(priceRoot(option));
  };

  document.addEventListener('input', onOptionInput);
  document.addEventListener('change', onOptionInput);
  document.addEventListener('on:variant:change', (event) => {
    const section = event.target.closest('.js-product');
    if (!section) return;

    const variantPrice = event.detail?.variant?.price;
    section.querySelectorAll('.js-product-price > .price, .js-atc-price > .price').forEach((priceEl) => {
      delete priceEl.dataset.surchargeApplied;
      if (variantPrice != null) priceEl.dataset.variantPrice = variantPrice;
    });
    updateSectionPrices(section);
  });

  const boot = (root) => {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('.js-product, quick-add-drawer').forEach(updateSectionPrices);
    if (scope.matches?.('.js-product, quick-add-drawer')) updateSectionPrices(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document));
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', (event) => boot(event.target));

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.matches?.('[data-custom-option-variant], .quick-add-drawer') || node.querySelector?.('[data-custom-option-variant]')) {
          updateSectionPrices(priceRoot(node) || node);
        }
      });
    });
  });

  if (document.body) observer.observe(document.body, { childList: true, subtree: true });
}
